import { useMemo, useState } from 'react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErroCarregamento from '../../components/common/ErroCarregamento';
import MultiSelect from '../../components/common/MultiSelect';
import { usePresentation } from '../../contexts/PresentationContext';
import { JANELA_VENCIMENTO_PADRAO_DIAS } from '../../utils/acreditacaoOpcoes';
import useDadosPortfolio from './useDadosPortfolio';
import { diasRestantesAte } from './dashboardHelpers';
import AbaVisaoGeralDashboard from './AbaVisaoGeralDashboard';
import PortfolioVencimentos from './PortfolioVencimentos';
import PortfolioCorretivas from './PortfolioCorretivas';

export default function DashboardOperador() {
  const [aba, setAba] = useState('visao-geral');
  const [hospitaisSelecionados, setHospitaisSelecionados] = useState(null);
  const dados = useDadosPortfolio();
  const { apresentacaoAtiva, hospitaisPermitidos } = usePresentation();

  // Memoizado com identidade estável entre renders (enquanto os dados brutos e a seleção não
  // mudam) — sem isso, os useMemo internos de AbaVisaoGeralDashboard recebiam um objeto novo
  // a cada render do pai e nunca conseguiam de fato pular o recálculo.
  const dadosPreparados = useMemo(() => {
    if (!dados.hospitais) return null;

    // Em modo apresentação, o universo de opções já é só os hospitais escolhidos na tela de
    // entrada — o filtro do dashboard passa a ajustar dentro desse subconjunto, nunca fora dele.
    const hospitaisNoUniverso = apresentacaoAtiva
      ? dados.hospitais.filter((h) => hospitaisPermitidos.includes(h.id_hospital))
      : dados.hospitais;
    const idsUniverso = hospitaisNoUniverso.map((h) => h.id_hospital);
    const idsSelecionados = (hospitaisSelecionados ?? idsUniverso).filter((id) => idsUniverso.includes(id));
    const hospitaisFiltrados = hospitaisNoUniverso.filter((h) => idsSelecionados.includes(h.id_hospital));
    const filtrarPorId = (mapa) =>
      Object.fromEntries(Object.entries(mapa).filter(([id]) => idsSelecionados.includes(id)));
    const dadosFiltrados = {
      hospitais: hospitaisFiltrados,
      conformidades: filtrarPorId(dados.conformidades),
      coberturas: filtrarPorId(dados.coberturas),
      avaliacoesPorHospital: filtrarPorId(dados.avaliacoesPorHospital),
      historicos: filtrarPorId(dados.historicos),
    };
    return { hospitaisNoUniverso, idsSelecionados, dadosFiltrados };
  }, [
    dados.hospitais,
    dados.conformidades,
    dados.coberturas,
    dados.avaliacoesPorHospital,
    dados.historicos,
    apresentacaoAtiva,
    hospitaisPermitidos,
    hospitaisSelecionados,
  ]);

  if (!dados.hospitais) {
    return dados.erro ? (
      <ErroCarregamento mensagem={dados.erro} onRetry={dados.recarregar} />
    ) : (
      <LoadingSpinner />
    );
  }

  const { hospitaisNoUniverso, idsSelecionados, dadosFiltrados } = dadosPreparados;

  const todasAvaliacoes = Object.values(dadosFiltrados.avaliacoesPorHospital).flat();
  const totalVencimentos = todasAvaliacoes.filter(
    (a) => diasRestantesAte(a.data_vencimento_vigencia) !== null && diasRestantesAte(a.data_vencimento_vigencia) <= JANELA_VENCIMENTO_PADRAO_DIAS
  ).length;
  const totalCorretivas = todasAvaliacoes.filter(
    (a) => diasRestantesAte(a.prazo_acao) !== null && diasRestantesAte(a.prazo_acao) <= JANELA_VENCIMENTO_PADRAO_DIAS
  ).length;

  return (
    <div>
      {dados.erroIndicadores && <div className="am-field__error mb-3">{dados.erroIndicadores}</div>}

      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
        <div className="filter-tabs">
          <button
            type="button"
            className={`filter-tab${aba === 'visao-geral' ? ' is-active' : ''}`}
            onClick={() => setAba('visao-geral')}
          >
            Visão Geral
          </button>
          <button
            type="button"
            className={`filter-tab${aba === 'vencimentos' ? ' is-active' : ''}`}
            onClick={() => setAba('vencimentos')}
          >
            Critérios próximos do vencimento{totalVencimentos > 0 ? ` (${totalVencimentos})` : ''}
          </button>
          <button
            type="button"
            className={`filter-tab${aba === 'corretivas' ? ' is-active' : ''}`}
            onClick={() => setAba('corretivas')}
          >
            Corretivas próximas do vencimento{totalCorretivas > 0 ? ` (${totalCorretivas})` : ''}
          </button>
        </div>

        <div className="d-flex align-items-center gap-2">
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--am-cinza-texto-2)', textTransform: 'uppercase' }}>
            Hospitais
          </span>
          <div style={{ minWidth: 220 }}>
            <MultiSelect
              options={hospitaisNoUniverso.map((h) => ({ value: h.id_hospital, label: h.nome }))}
              selected={idsSelecionados}
              onChange={setHospitaisSelecionados}
              placeholder="Buscar hospital..."
            />
          </div>
        </div>
      </div>

      {aba === 'visao-geral' && <AbaVisaoGeralDashboard {...dadosFiltrados} />}
      {aba === 'vencimentos' && <PortfolioVencimentos {...dadosFiltrados} />}
      {aba === 'corretivas' && <PortfolioCorretivas {...dadosFiltrados} />}
    </div>
  );
}
